import { Injectable } from '@angular/core';
import { HttpRequest, HttpHandler, HttpEvent, HttpInterceptor, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError, BehaviorSubject } from 'rxjs';
import { catchError, filter, take, switchMap } from 'rxjs/operators';
import { AuthService } from '../../services/auth/auth.service';
import { TenantService } from '../../services/tenant/tenant.service';
import { Router } from '@angular/router';
import { NotificationService } from '../../services/notification/notification.service';
import { environment } from 'src/environments/environment';

@Injectable()
export class ErrorsInterceptor implements HttpInterceptor {

  private isRefreshing = false;
  private refreshTokenSubject = new BehaviorSubject<string | null>(null);

  constructor(
    private router: Router,
    private authService: AuthService,
    private notificationService: NotificationService,
    private tenantService: TenantService
  ) { }

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    const isRefreshUrl = request.url.includes(`auth/${environment.actions.refresh}`);
    const isLoginUrl = request.url.includes(`auth/${environment.actions.login}`);

    return next.handle(request).pipe(
      catchError((error: HttpErrorResponse) => {
        if (error.status === 403 && error.error?.code === 'TENANT_INACTIVE') {
          this.notificationService.errorMessage('Esta empresa se encuentra inactiva. Contacta a soporte para reactivarla.');
          this.forceLogout();
          return throwError(error);
        }
        if (error.status === 401 && this.authService.getAccesToken() && !isRefreshUrl && !isLoginUrl) {
          return this.handle401Error(request, next);
        }
        if (error.status === 401 && !isLoginUrl) {
          this.forceLogout();
        }
        return throwError(error);
      })
    );
  }

  private handle401Error(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    if (this.isRefreshing) {
      return this.refreshTokenSubject.pipe(
        filter(token => token !== null),
        take(1),
        switchMap(token => next.handle(this.addToken(request, token!)))
      );
    }

    this.isRefreshing = true;
    this.refreshTokenSubject.next(null);

    return this.authService.refreshToken().pipe(
      switchMap((res: any) => {
        this.isRefreshing = false;
        this.refreshTokenSubject.next(res.access_token);
        return next.handle(this.addToken(request, res.access_token));
      }),
      catchError(err => {
        this.isRefreshing = false;
        this.forceLogout();
        return throwError(err);
      })
    );
  }

  private forceLogout(): void {
    const isAdminPath = this.router.url.startsWith('/admin');
    const slug = this.tenantService.getSlug();
    localStorage.clear();
    this.authService.setUserLogged$(undefined);
    if (isAdminPath) {
      this.router.navigate(['/admin', 'auth', 'login']);
    } else {
      this.router.navigate(['/', slug, 'auth', 'login']);
    }
  }

  private addToken(request: HttpRequest<unknown>, token: string): HttpRequest<unknown> {
    return request.clone({
      headers: request.headers.set('Authorization', 'Bearer ' + token)
    });
  }
}
